// ---------- le thème ----------
//
// Trois habillages de l'interface, au choix du joueur : « Clair » (le défaut), « Ciel » et
// « Soir ». Ce module ne porte que le choix et de quoi le MONTRER — les valeurs de couleur
// vivent dans les blocs [data-skin] de css/style.css, qui est l'endroit où vit la charte.
// settings.ts pose l'attribut sur <html>, le CSS fait le reste : aucune règle en aval ne sait
// lequel des trois est actif.
//
// Pourquoi trois et pas un interrupteur clair/sombre : les deux thèmes clairs ne sont pas la
// même proposition. « Clair » garde l'identité mûre/citron de l'application, retournée vers la
// lumière — c'est le même jeu, mieux éclairé. « Ciel » est un dégradé pêche → lavande avec des
// panneaux transparents, nettement plus doux et nettement moins neutre. Les fondre dans un seul
// bouton reviendrait à choisir à la place du joueur laquelle des deux est LA version claire.
//
// « Soir » n'est pas l'ancien thème remis tel quel : le reproche des joueurs ne portait pas sur
// le sombre en soi mais sur un sombre froid rehaussé d'un citron acide. Il est ici bleu-nuit
// tiède et ambre — sombre pour jouer le soir, sans le rendu « tableau de bord ».

import { t } from './i18n';

export type SkinId = 'clair' | 'ciel' | 'soir';

export interface Skin {
  id: SkinId;
  label: string;
  /** Le sol du thème, tel que la pastille des réglages le montre — cf. buildSkins() dans
   *  prefs.ts. Ces valeurs DOUBLENT celles de la charte, et c'est voulu : une pastille doit
   *  montrer le thème qu'elle propose, pas celui qui est actif, donc elle ne peut pas lire
   *  var(--bg). Elles n'ont à être exactes qu'à l'œil, pas au chiffre. */
  ground: string;
  /** Sa couleur d'accent, la case posée au milieu de la pastille. */
  accent: string;
  /** La couleur que le système peint autour de la page (`<meta name="theme-color">`, barre
   *  d'état d'Android). Le fond du thème, en une seule valeur opaque — un dégradé n'a pas de
   *  sens pour une barre système. */
  chrome: string;
}

/** Le clair par défaut, et c'est le cœur de ce changement : l'application ouvrait sur un fond
 *  mûre qui tenait 90 % de l'écran, et c'est ce que les joueurs ont appelé « austère ». */
export const DEFAULT_SKIN: SkinId = 'clair';

export const SKINS: Skin[] = [
  {
    id: 'clair',
    label: t('skin.clair'),
    ground: 'linear-gradient(160deg, #ffffff 0%, #ded3f7 100%)',
    accent: '#7b5cf0',
    chrome: '#ded3f7',
  },
  {
    id: 'ciel',
    label: t('skin.ciel'),
    ground: 'linear-gradient(160deg, #ffd3b4 0%, #ffd7e4 52%, #d9d2f7 100%)',
    accent: '#f2718c',
    chrome: '#d9d2f7',
  },
  {
    id: 'soir',
    label: t('skin.soir'),
    ground: 'linear-gradient(160deg, #2e4162 0%, #1a2334 100%)',
    accent: '#ffc46b',
    chrome: '#1a2334',
  },
];

export function skinById(id: string): Skin {
  return SKINS.find((s) => s.id === id) ?? SKINS[0];
}
